import { dehydrate } from '@tanstack/react-query'
import { getProps } from 'ssr/getProps'
import { mapsSortOptions, USER_PROFILE_TAB } from 'ui-components'
import { getUserByIdQuery } from 'user-auth'
import { getUserLink } from 'utils'
import { runWithAmplifyServerContext, serverClient } from 'web-amplify/server'
import { mapsPerPage } from '~user-profile/constants'
import { getUserByIdQueryKey } from '~user-profile/queries/useGetUserById'
import {
  searchMapsQuery,
  searchMapsQueryKey
} from '~user-profile/queries/useSearchMaps'
import { Sort_Order } from '~user-profile/types/__generated/gql/graphql'

type Query = {
  userId?: string
  username?: string
  tab?: string
  query?: string
  page?: string
  tags?: string
  sortBy?: string
}

export const getServerSideProps = getProps(async (context, client) => {
  const {
    userId,
    username,
    tab,
    query = '',
    tags = '',
    sortBy = mapsSortOptions[1]!.value
  } = context.query as Query

  const page = Number(context.query.page)

  if (!userId || !username) {
    return {
      notFound: true
    }
  }

  if (!tab) {
    return {
      redirect: {
        destination: getUserLink({
          userId,
          username,
          tab: USER_PROFILE_TAB.MAPS,
          page: 1
        }),
        permanent: false
      }
    }
  }

  const [field, order] = (sortBy || mapsSortOptions[1]!.value)
    .toString()
    .split('|') as [string, Sort_Order]

  const tagIds = tags?.toString().split(',').filter(Boolean) || []

  const searchMapsVariables = {
    creatorId: userId,
    limit: mapsPerPage,
    offset: (Number(page) - 1) * mapsPerPage,
    query,
    tagIds,
    sortBy: { field, order }
  }

  const [
    {
      data: { getUserById }
    },
    {
      data: { searchMaps }
    }
  ] = (await runWithAmplifyServerContext({
    nextServerContext: { request: context.req, response: context.res },
    operation: async (ctx) =>
      Promise.all([
        serverClient.graphql(ctx, {
          query: getUserByIdQuery,
          variables: { id: userId }
        }),
        serverClient.graphql(ctx, {
          query: searchMapsQuery,
          variables: searchMapsVariables
        })
      ])
  })) as any

  await Promise.all([
    client.prefetchQuery({
      queryKey: getUserByIdQueryKey(userId),
      queryFn: () => getUserById
    }),

    client.prefetchQuery({
      queryKey: searchMapsQueryKey(searchMapsVariables),
      queryFn: () => searchMaps
    })
  ])

  const { total = 0 } = searchMaps as {
    total?: number
  }

  const hasNextPage = total > page * mapsPerPage

  return {
    props: {
      dehydratedState: dehydrate(client),
      page,
      hasNextPage
    }
  }
})
